import dayjs from "dayjs";
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Clock,
  ExternalLink,
  MapPin,
  MoreHorizontal,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { HoursModal } from "./HoursModal";
import {
  PlacesQueryResult,
  PlacesQueryResultBusinessStatus,
  PlacesQueryResultPriceLevel,
  PlacesQueryResultSortField,
  PlacesQueryResultsSearchParams,
} from "../../../../../models/PlacesQueryData";
import ColumnFilterPopover from "../../../../Common/ColumnFilterPopover";
import ConfigurableTableLayout from "../../../../Common/ConfigurableTableLayout";
import InputBox from "../../../../FormElement/InputBox";
import { PlacesQueryResultsFilterFormData } from "../../hooks";

import "./styles.scss";

export interface IResultsTableProps {
  results: PlacesQueryResult[];
  totalCount: number;
  searchParams: PlacesQueryResultsSearchParams;
  setSearchParams: React.Dispatch<
    React.SetStateAction<PlacesQueryResultsSearchParams>
  >;
  filterFormData: PlacesQueryResultsFilterFormData;
  onChangeFilterFormData: (data: PlacesQueryResultsFilterFormData) => void;
}

const EMPTY_VALUE = "-";

type ResultColumnId =
  | "name"
  | "address"
  | "phone"
  | "rating"
  | "user_rating_count"
  | "business_status"
  | "price_level"
  | "created_at"
  | "actions";

const ALL_COLUMNS: ResultColumnId[] = [
  "name",
  "address",
  "phone",
  "rating",
  "user_rating_count",
  "business_status",
  "price_level",
  "created_at",
  "actions",
];

const DEFAULT_VISIBLE_COLUMNS: ResultColumnId[] = [...ALL_COLUMNS];
const VISIBLE_COLUMNS_STORAGE_KEY = "GooglePlacesResultsTable_VisibleColumns";

const COLUMN_WIDTHS: Record<ResultColumnId, string> = {
  name: "minmax(220px, 1fr)",
  address: "260px",
  phone: "140px",
  rating: "120px",
  user_rating_count: "110px",
  business_status: "220px",
  price_level: "160px",
  created_at: "160px",
  actions: "70px",
};

const REQUIRED_COLUMNS: ResultColumnId[] = ["name", "actions"];

const SORTABLE_COLUMNS: PlacesQueryResultSortField[] = [
  "name",
  "rating",
  "user_rating_count",
  "created_at",
];

const BUSINESS_STATUSES: PlacesQueryResultBusinessStatus[] = [
  "OPERATIONAL",
  "CLOSED_TEMPORARILY",
  "CLOSED_PERMANENTLY",
];

const PRICE_LEVELS: PlacesQueryResultPriceLevel[] = [
  "PRICE_LEVEL_FREE",
  "PRICE_LEVEL_INEXPENSIVE",
  "PRICE_LEVEL_MODERATE",
  "PRICE_LEVEL_EXPENSIVE",
  "PRICE_LEVEL_VERY_EXPENSIVE",
];

const MIN_RATINGS = [1, 2, 3, 4, 4.5];

const normalizeVisibleColumns = (value: unknown): ResultColumnId[] => {
  if (!Array.isArray(value)) {
    return DEFAULT_VISIBLE_COLUMNS;
  }

  const filtered = value.filter((col): col is ResultColumnId =>
    ALL_COLUMNS.includes(col as ResultColumnId)
  );
  const unique = Array.from(new Set(filtered));

  if (unique.length === 0) {
    return DEFAULT_VISIBLE_COLUMNS;
  }

  const required = new Set<ResultColumnId>(REQUIRED_COLUMNS);
  unique.forEach((item) => required.add(item));

  return ALL_COLUMNS.filter((item) => required.has(item));
};

export const ResultsTable: React.FunctionComponent<IResultsTableProps> = ({
  results,
  totalCount,
  searchParams,
  setSearchParams,
  filterFormData,
  onChangeFilterFormData,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`internalToolsHomePage.gbp_places_query.detail.table.${key}`);
  const tHeader = (key: string) =>
    _t(
      `internalToolsHomePage.gbp_places_query.detail.table.header.${key}.default`
    );
  const tFilter = (key: string, params?: { [key: string]: string | number }) =>
    _t(`internalToolsHomePage.gbp_places_query.detail.filter.${key}`, params);
  const tBusinessStatus = (key: string) =>
    _t(
      `internalToolsHomePage.gbp_places_query.detail.business_status_values.${key}`
    );
  const tPriceLevel = (key: string) =>
    _t(
      `internalToolsHomePage.gbp_places_query.detail.price_level_values.${key}`
    );

  const [visibleColumns, setVisibleColumns] = useState<ResultColumnId[]>(() => {
    const savedColumns = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY);
    if (!savedColumns) {
      return DEFAULT_VISIBLE_COLUMNS;
    }

    try {
      return normalizeVisibleColumns(JSON.parse(savedColumns));
    } catch {
      return DEFAULT_VISIBLE_COLUMNS;
    }
  });
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [openFilterColumnId, setOpenFilterColumnId] =
    useState<ResultColumnId | null>(null);
  const [hoursModalTarget, setHoursModalTarget] = useState<
    PlacesQueryResult | undefined
  >();
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState(searchParams.name ?? "");

  useEffect(() => {
    setNameInput(searchParams.name ?? "");
  }, [searchParams.name]);

  const toggleColumnVisibility = (columnId: ResultColumnId) => {
    if (REQUIRED_COLUMNS.includes(columnId)) {
      return;
    }

    setVisibleColumns((prev) => {
      const next = prev.includes(columnId)
        ? normalizeVisibleColumns(prev.filter((col) => col !== columnId))
        : normalizeVisibleColumns([...prev, columnId]);

      localStorage.setItem(VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const toggleFilter = (columnId: ResultColumnId) => {
    setOpenFilterColumnId((cur) => (cur === columnId ? null : columnId));
  };

  const onSelectSortField = (field: PlacesQueryResultSortField) => {
    setSearchParams((sp) => {
      if (sp.sort === field) {
        return { ...sp, order: sp.order === "asc" ? "desc" : "asc" };
      }
      return { ...sp, sort: field, order: "asc" };
    });
  };

  const createSortButton = (field: PlacesQueryResultSortField) => {
    const isActive = searchParams.sort === field;
    const order = searchParams.order ?? "asc";
    const label = tHeader(field);

    return (
      <button
        type="button"
        className={`refactored-sort-button ${isActive ? "active" : ""}`}
        onClick={() => onSelectSortField(field)}
      >
        <span>{label}</span>
        <span
          className={`sort-icon-wrap ${isActive ? "is-active" : "is-idle"}`}
          aria-hidden="true"
        >
          {isActive ? (
            order === "desc" ? (
              <ChevronDown className="sort-icon" size={14} strokeWidth={2} />
            ) : (
              <ChevronUp className="sort-icon" size={14} strokeWidth={2} />
            )
          ) : (
            <ChevronsUpDown className="sort-icon" size={14} strokeWidth={2} />
          )}
        </span>
      </button>
    );
  };

  const businessStatusOptions: {
    value: PlacesQueryResultBusinessStatus | "";
    label: string;
  }[] = [
    { value: "", label: tFilter("business_status_all") },
    ...BUSINESS_STATUSES.map((status) => ({
      value: status,
      label: tBusinessStatus(status),
    })),
  ];

  const priceLevelOptions: {
    value: PlacesQueryResultPriceLevel | "";
    label: string;
  }[] = [
    { value: "", label: tFilter("price_level_all") },
    ...PRICE_LEVELS.map((level) => ({
      value: level,
      label: tPriceLevel(level),
    })),
  ];

  const minRatingOptions: { value: number | ""; label: string }[] = [
    { value: "", label: tFilter("min_rating_all") },
    ...MIN_RATINGS.map((rating) => ({
      value: rating,
      label: tFilter("min_rating_option", { rating }),
    })),
  ];

  const handleApplyName = () => {
    setSearchParams((sp) => ({
      ...sp,
      name: nameInput.trim() || undefined,
      offset: 0,
    }));
    setOpenFilterColumnId(null);
  };

  const handleClearName = () => {
    setNameInput("");
    setSearchParams((sp) => ({ ...sp, name: undefined, offset: 0 }));
    setOpenFilterColumnId(null);
  };

  const handleChangeBusinessStatus = (
    value: PlacesQueryResultBusinessStatus | ""
  ) => {
    setSearchParams((sp) => ({
      ...sp,
      business_status: value || undefined,
      offset: 0,
    }));
    setOpenFilterColumnId(null);
  };

  const handleChangePriceLevel = (value: PlacesQueryResultPriceLevel | "") => {
    setSearchParams((sp) => ({
      ...sp,
      price_level: value || undefined,
      offset: 0,
    }));
    setOpenFilterColumnId(null);
  };

  const handleChangeMinRating = (value: number | "") => {
    setSearchParams((sp) => ({
      ...sp,
      min_rating: value || undefined,
      offset: 0,
    }));
    setOpenFilterColumnId(null);
  };

  const hasActiveNameFilter = (searchParams.name ?? "").trim() !== "";

  const hasActiveFilters =
    hasActiveNameFilter ||
    !!searchParams.business_status ||
    !!searchParams.price_level ||
    !!searchParams.min_rating ||
    searchParams.sort !== "created_at" ||
    searchParams.order !== "desc";

  const handleClearAllFilters = () => {
    setNameInput("");
    setSearchParams({
      sort: "created_at",
      order: "desc",
      limit: searchParams.limit,
      offset: 0,
      seek_total_count: true,
    });
    setOpenFilterColumnId(null);
  };

  const renderColumnFilter = (columnId: ResultColumnId) => {
    switch (columnId) {
      case "name":
        return (
          <ColumnFilterPopover
            isActive={hasActiveNameFilter}
            isOpen={openFilterColumnId === "name"}
            onToggle={() => toggleFilter("name")}
            onRequestClose={() => setOpenFilterColumnId(null)}
            ariaLabel={tFilter("search_placeholder")}
          >
            <InputBox
              isSearch
              placeholder={tFilter("search_placeholder")}
              value={nameInput}
              onChange={setNameInput}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleApplyName();
                }
              }}
            />
            <div className="common-column-filter-actions">
              <button
                type="button"
                className="btn btn-border"
                onClick={handleApplyName}
              >
                {tFilter("apply")}
              </button>
              {hasActiveNameFilter && (
                <button
                  type="button"
                  className="btn btn-border btn-border-red"
                  onClick={handleClearName}
                >
                  {tFilter("clear")}
                </button>
              )}
            </div>
          </ColumnFilterPopover>
        );
      case "business_status":
        return (
          <ColumnFilterPopover
            isActive={!!searchParams.business_status}
            isOpen={openFilterColumnId === "business_status"}
            onToggle={() => toggleFilter("business_status")}
            onRequestClose={() => setOpenFilterColumnId(null)}
            ariaLabel={tFilter("business_status_all")}
          >
            <div className="common-column-filter-options">
              {businessStatusOptions.map((option) => (
                <button
                  key={option.value || "all"}
                  type="button"
                  className={`common-column-filter-option ${
                    (searchParams.business_status ?? "") === option.value
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => handleChangeBusinessStatus(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </ColumnFilterPopover>
        );
      case "price_level":
        return (
          <ColumnFilterPopover
            isActive={!!searchParams.price_level}
            isOpen={openFilterColumnId === "price_level"}
            onToggle={() => toggleFilter("price_level")}
            onRequestClose={() => setOpenFilterColumnId(null)}
            ariaLabel={tFilter("price_level_all")}
          >
            <div className="common-column-filter-options">
              {priceLevelOptions.map((option) => (
                <button
                  key={option.value || "all"}
                  type="button"
                  className={`common-column-filter-option ${
                    (searchParams.price_level ?? "") === option.value
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => handleChangePriceLevel(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </ColumnFilterPopover>
        );
      case "rating":
        return (
          <ColumnFilterPopover
            isActive={!!searchParams.min_rating}
            isOpen={openFilterColumnId === "rating"}
            onToggle={() => toggleFilter("rating")}
            onRequestClose={() => setOpenFilterColumnId(null)}
            ariaLabel={tFilter("min_rating_all")}
          >
            <div className="common-column-filter-options">
              {minRatingOptions.map((option) => (
                <button
                  key={option.value || "all"}
                  type="button"
                  className={`common-column-filter-option ${
                    (searchParams.min_rating ?? "") === option.value
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => handleChangeMinRating(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </ColumnFilterPopover>
        );
      default:
        return null;
    }
  };

  const getDynamicGridTemplateStyle = () => ({
    gridTemplateColumns: visibleColumns
      .map((col) => COLUMN_WIDTHS[col])
      .join(" "),
  });

  return (
    <>
      <ConfigurableTableLayout
        className="gbp-places-query-results-table-layout"
        onContainerClick={() => {
          setShowColumnSettings(false);
          setOpenActionMenuId(null);
          setOpenFilterColumnId(null);
        }}
        onClearAllConditions={
          hasActiveFilters ? handleClearAllFilters : undefined
        }
        clearAllLabel={tFilter("clear_all")}
        controls={
          <div
            className="column-settings-container"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="btn-column-settings"
              onClick={() => setShowColumnSettings((prev) => !prev)}
            >
              {t("column_settings")} ⚙️
            </button>
            {showColumnSettings && (
              <div className="column-settings-dropdown">
                <div className="dropdown-title">
                  {t("column_settings_title")}
                </div>
                {ALL_COLUMNS.map((colId) => (
                  <label key={`setting-${colId}`} className="setting-label">
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes(colId)}
                      disabled={REQUIRED_COLUMNS.includes(colId)}
                      onChange={() => toggleColumnVisibility(colId)}
                    />
                    <span className="checkbox-custom"></span>
                    <span className="label-text">
                      {colId === "actions"
                        ? t("actions_column_label")
                        : t(colId)}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        }
        tableContent={
          <div className="top-scrollbar">
            <div
              className="table-data table-data-grid gbp-places-query-results-table scrollable"
              style={getDynamicGridTemplateStyle()}
            >
              <div className="row-th refactored-facility-list-header">
                {visibleColumns.map((columnId) => (
                  <div
                    className={`col-th column-${columnId}`}
                    key={`header-${columnId}`}
                  >
                    <div className="spacing header-cell-inner">
                      {SORTABLE_COLUMNS.includes(
                        columnId as PlacesQueryResultSortField
                      ) ? (
                        createSortButton(columnId as PlacesQueryResultSortField)
                      ) : (
                        <span className="column-label-text">{t(columnId)}</span>
                      )}
                      {renderColumnFilter(columnId)}
                    </div>
                  </div>
                ))}
              </div>
              {results.length === 0 ? (
                <div className="row-td text-center sub-txt">
                  {_t(
                    "internalToolsHomePage.gbp_places_query.detail.results_empty"
                  )}
                </div>
              ) : (
                results.map((result) => {
                  const rating = result.rating
                    ? parseFloat(result.rating)
                    : null;
                  const weekdayDescriptions =
                    result.regular_opening_hours?.weekdayDescriptions;
                  return (
                    <div className="row-td" key={result.id}>
                      {visibleColumns.includes("name") && (
                        <div className="col-td column-name">
                          <div
                            className="spacing cell-ellipsis"
                            title={result.name || undefined}
                          >
                            {result.name || EMPTY_VALUE}
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("address") && (
                        <div className="col-td column-address">
                          <div
                            className="spacing cell-ellipsis"
                            title={result.formatted_address || undefined}
                          >
                            {result.formatted_address || EMPTY_VALUE}
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("phone") && (
                        <div className="col-td column-phone">
                          <div
                            className="spacing cell-ellipsis"
                            title={
                              result.national_phone_number ||
                              result.international_phone_number ||
                              undefined
                            }
                          >
                            {result.national_phone_number ||
                              result.international_phone_number ||
                              EMPTY_VALUE}
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("rating") && (
                        <div className="col-td column-rating">
                          <div className="spacing">
                            {rating !== null ? rating : EMPTY_VALUE}
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("user_rating_count") && (
                        <div className="col-td column-user_rating_count">
                          <div className="spacing">
                            {result.user_rating_count !== null
                              ? result.user_rating_count
                              : EMPTY_VALUE}
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("business_status") && (
                        <div className="col-td column-business_status">
                          <div
                            className="spacing cell-ellipsis"
                            title={result.business_status || undefined}
                          >
                            {result.business_status || EMPTY_VALUE}
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("price_level") && (
                        <div className="col-td column-price_level">
                          <div
                            className="spacing cell-ellipsis"
                            title={result.price_level || undefined}
                          >
                            {result.price_level || EMPTY_VALUE}
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("created_at") && (
                        <div className="col-td column-created_at">
                          <div className="spacing">
                            {result.created_at
                              ? dayjs(result.created_at).format(
                                  "YYYY/MM/DD HH:mm"
                                )
                              : EMPTY_VALUE}
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("actions") && (
                        <div className="col-td center buttons-section column-actions">
                          <div
                            className="action-menu"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <button
                              type="button"
                              className="action-menu-trigger"
                              aria-label={t("actions_column_label")}
                              aria-expanded={openActionMenuId === result.id}
                              onClick={() => {
                                setOpenActionMenuId((currentId) =>
                                  currentId === result.id ? null : result.id
                                );
                              }}
                            >
                              <MoreHorizontal size={20} aria-hidden="true" />
                            </button>
                            {openActionMenuId === result.id && (
                              <div className="action-menu-dropdown">
                                {weekdayDescriptions?.length ? (
                                  <button
                                    type="button"
                                    className="action-menu-item"
                                    onClick={() => {
                                      setOpenActionMenuId(null);
                                      setHoursModalTarget(result);
                                    }}
                                  >
                                    <Clock size={16} aria-hidden="true" />
                                    <span>{t("view_hours")}</span>
                                  </button>
                                ) : null}
                                {result.website_uri && (
                                  <a
                                    className="action-menu-item"
                                    href={result.website_uri}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => setOpenActionMenuId(null)}
                                  >
                                    <ExternalLink
                                      size={16}
                                      aria-hidden="true"
                                    />
                                    <span>{t("website_link_text")}</span>
                                  </a>
                                )}
                                {result.google_maps_uri && (
                                  <a
                                    className="action-menu-item"
                                    href={result.google_maps_uri}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => setOpenActionMenuId(null)}
                                  >
                                    <MapPin size={16} aria-hidden="true" />
                                    <span>{t("maps_link_text")}</span>
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        }
        pagination={{
          filterFormData,
          totalRowsNumber: totalCount,
          onChangeFilterFormData,
        }}
      />
      {hoursModalTarget?.regular_opening_hours?.weekdayDescriptions && (
        <HoursModal
          name={hoursModalTarget.name || EMPTY_VALUE}
          weekdayDescriptions={
            hoursModalTarget.regular_opening_hours.weekdayDescriptions
          }
          onClose={() => setHoursModalTarget(undefined)}
        />
      )}
    </>
  );
};

export default ResultsTable;
